import { useState, useEffect, useCallback, useRef } from 'react';

const EXIT_INTENT_STORAGE_KEY = 'va_sprouts_exit_intent_dismissed';

/**
 * Hook that detects exit intent (mouse leaves viewport toward top).
 * Shows only once per session and respects a localStorage flag to avoid
 * repeatedly bothering the same user across sessions.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether exit intent detection should be active
 * @param {number} options.threshold - Y-coordinate threshold to trigger (default 10)
 * @param {number} options.delayMs - Minimum time on page before activation (default 3000)
 * @returns {{ showModal: boolean, dismiss: () => void }}
 */
const useExitIntent = ({ enabled = true, threshold = 10, delayMs = 3000 } = {}) => {
  const [showModal, setShowModal] = useState(false);
  const hasTriggered = useRef(false);
  const pageLoadTime = useRef(Date.now());

  const dismiss = useCallback(() => {
    setShowModal(false);
    hasTriggered.current = true;
    try {
      sessionStorage.setItem(EXIT_INTENT_STORAGE_KEY, 'true');
    } catch {
      // Ignore storage errors
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Don't show if already dismissed this session
    try {
      if (sessionStorage.getItem(EXIT_INTENT_STORAGE_KEY) === 'true') {
        hasTriggered.current = true;
        return;
      }
    } catch {
      // Ignore storage errors
    }

    const handleMouseLeave = (e) => {
      if (hasTriggered.current) return;

      // Only trigger when mouse exits toward the top of the viewport
      if (e.clientY > threshold) return;

      // Require minimum time on the page before triggering
      if (Date.now() - pageLoadTime.current < delayMs) return;

      hasTriggered.current = true;
      setShowModal(true);
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, threshold, delayMs]);

  return { showModal, dismiss };
};

export default useExitIntent;
